import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { helpCategories, type HelpCategory } from './helpContent'
import { HelpArticle } from './HelpArticle'

interface HelpPageProps {
  onClose: () => void
}

/**
 * Full-page help view with category overview and drill-down to articles.
 * Shown over the normal tab content when user opens Help from More tab.
 */
export function HelpPage({ onClose }: HelpPageProps) {
  const { t } = useTranslation()
  const [activeCategory, setActiveCategory] = useState<HelpCategory | null>(null)

  // Show article detail
  if (activeCategory) {
    return (
      <HelpArticle
        category={activeCategory}
        onBack={() => setActiveCategory(null)}
      />
    )
  }

  // Category overview
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 px-3 py-2">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:underline mb-1"
        >
          <ArrowLeft size={12} />
          {t('help.backToMore')}
        </button>
        <h1 className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
          {t('help.title')}
        </h1>
        <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5">
          {t('help.subtitle')}
        </p>
      </div>

      {/* Category list */}
      <div className="px-3 py-2 space-y-1.5">
        {helpCategories.map((category) => {
          const Icon = category.icon
          return (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category)}
              className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:border-primary-200 dark:hover:border-primary-800 transition-all text-left group"
            >
              <div className="flex-shrink-0 w-7 h-7 rounded-md bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50 transition-colors">
                <Icon size={14} className="text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-neutral-800 dark:text-neutral-200 leading-snug">
                  {t(category.titleKey)}
                </p>
                <p className="text-[10px] text-neutral-500 dark:text-neutral-400 leading-snug mt-0.5 truncate">
                  {t(category.descriptionKey)}
                </p>
              </div>
              <ChevronRight size={14} className="text-neutral-300 dark:text-neutral-600 shrink-0 group-hover:text-primary-400 transition-colors" />
            </button>
          )
        })}
      </div>
    </div>
  )
}
